import { useEffect, useState } from 'react';
import { deckBoards, exportToDeck, fetchTodos, type DeckBoard, type Todo } from '../api';

/**
 * Turning a meeting's tasks into Deck cards, one review step in between.
 *
 * The review is the feature. A task says "bis Freitag" because somebody said
 * "bis Freitag", and the analysis deliberately does no date arithmetic - it
 * does not know which Friday, and a wrong due date is worse than none. So the
 * spoken wording is shown as a hint and the date is picked here, by the person
 * who was in the meeting.
 *
 * The same goes for who it belongs to: the protocol knows a name, Deck wants an
 * account, and two colleagues called Julia are not a problem a matcher should
 * solve on its own.
 */

interface Props {
  jobId: string;
  token: string;
}

interface Draft {
  include: boolean;
  title: string;
  description: string;
  assignUserId: string;
  dueDate: string;
  /** What was actually said about timing, kept visible next to the date field. */
  spokenDue?: string;
  /**
   * The name from the protocol, kept so the suggestion can be made again.
   *
   * It has to be: who is on the board differs per board, so a name that matched
   * an account on one may match nothing - or somebody else - on the next.
   */
  owner?: string;
}

/**
 * Picks the likeliest board member for a name from the transcript.
 *
 * A suggestion, never a decision - the field stays editable and an unclear case
 * simply stays empty. Matching is on the parts of the account name, so
 * "christian.braun" is found by "Christian" and by "Braun", and a bare first
 * name that fits two accounts picks neither.
 */
export function suggestAssignee(owner: string | undefined, members: string[]): string {
  const name = (owner ?? '').toLowerCase().trim();
  if (!name || name === 'unbekannt') return '';

  const parts = name.split(/\s+/).filter((part) => part.length > 2);
  if (parts.length === 0) return '';

  const matches = members.filter((member) => {
    const pieces = member.toLowerCase().split(/[._-]/);
    return parts.some((part) => pieces.includes(part));
  });

  return matches.length === 1 ? (matches[0] as string) : '';
}

function draftFrom(todo: Todo, members: string[]): Draft {
  const lines = [
    todo.quote ? `> ${todo.quote}` : undefined,
    todo.timestamp ? `_bei ${todo.timestamp}_` : undefined,
    todo.condition ? `**Voraussetzung:** ${todo.condition}` : undefined,
  ].filter(Boolean);

  return {
    include: true,
    title: todo.task,
    description: lines.join('\n\n'),
    assignUserId: suggestAssignee(todo.owner, members),
    dueDate: '',
    spokenDue: todo.dueDate ?? undefined,
    owner: todo.owner,
  };
}

export function DeckExport({ jobId, token }: Props) {
  const [open, setOpen] = useState(false);
  const [boards, setBoards] = useState<DeckBoard[]>();
  const [unknownAccount, setUnknownAccount] = useState(false);
  const [boardId, setBoardId] = useState<number>();
  const [stackId, setStackId] = useState<number>();
  const [drafts, setDrafts] = useState<Draft[]>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<string>();

  const board = boards?.find((entry) => entry.id === boardId);

  useEffect(() => {
    if (!open || boards) return;
    void (async () => {
      try {
        const result = await deckBoards(token);
        setBoards(result.boards);
        setUnknownAccount(result.reason === 'unknown-account');
        const first = result.boards[0];
        if (first) {
          setBoardId(first.id);
          setStackId(first.stacks[0]?.id);
        }
        const todos = await fetchTodos(token, jobId);
        setDrafts(todos.map((todo) => draftFrom(todo, first?.members ?? [])));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Konnte Deck nicht erreichen');
      }
    })();
  }, [open, boards, token, jobId]);

  /**
   * Switching board also redoes the assignee suggestions.
   *
   * Needed because members differ per board. Left alone, a name suggested for
   * the first board would stay selected on the next - where that account may not
   * be a member at all, which shows as an empty-looking dropdown that is in fact
   * holding a value the server will then drop. Anything the person picked by
   * hand is equally affected, and there is no way to tell the two apart, so the
   * honest move is to suggest again and let them look.
   */
  function chooseBoard(id: number) {
    setBoardId(id);
    const next = boards?.find((entry) => entry.id === id);
    setStackId(next?.stacks[0]?.id);
    setDrafts((current) =>
      current?.map((draft) => ({
        ...draft,
        assignUserId: suggestAssignee(draft.owner, next?.members ?? []),
      })),
    );
  }

  function patch(index: number, change: Partial<Draft>) {
    setDrafts((current) =>
      current?.map((draft, i) => (i === index ? { ...draft, ...change } : draft)),
    );
  }

  async function submit() {
    if (!boardId || !stackId || !drafts) return;
    const selected = drafts.filter((draft) => draft.include && draft.title.trim());
    if (selected.length === 0) return;

    setBusy(true);
    setError(undefined);
    try {
      const result = await exportToDeck(token, jobId, {
        boardId,
        stackId,
        cards: selected.map((draft) => ({
          title: draft.title.trim(),
          description: draft.description,
          assignUserId: draft.assignUserId || undefined,
          dueDate: draft.dueDate ? new Date(`${draft.dueDate}T12:00:00`).toISOString() : undefined,
        })),
      });
      const failed = result.created.filter((entry) => entry.failed).length;
      setDone(
        failed === 0
          ? `${result.created.length} Karte(n) angelegt.`
          : `${result.created.length - failed} von ${result.created.length} Karten angelegt.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Übernahme fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button className="secondary" onClick={() => setOpen(true)}>
        Aufgaben nach Deck übernehmen
      </button>
    );
  }

  return (
    <section className="deck-export">
      <h4>Nach Deck übernehmen</h4>

      {unknownAccount && (
        <p className="muted small">
          Zu diesem Konto ist kein Nextcloud-Benutzer hinterlegt. Ohne den lässt sich nicht prüfen,
          ob du Mitglied eines Boards bist.
        </p>
      )}

      {boards?.length === 0 && !unknownAccount && (
        <p className="muted small">
          Noch kein Board freigegeben. Gib in Deck das gewünschte Board für <code>protokoll-bot</code>{' '}
          frei — danach erscheint es hier.
        </p>
      )}

      {board && (
        <div className="deck-target">
          <label>
            Board
            <select value={boardId} onChange={(e) => chooseBoard(Number(e.target.value))}>
              {boards?.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.title}
                </option>
              ))}
            </select>
          </label>
          <label>
            Stapel
            <select value={stackId} onChange={(e) => setStackId(Number(e.target.value))}>
              {board.stacks.map((stack) => (
                <option key={stack.id} value={stack.id}>
                  {stack.title}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {drafts?.map((draft, index) => (
        <div key={index} className={`deck-card ${draft.include ? '' : 'excluded'}`}>
          <label className="deck-include">
            <input
              type="checkbox"
              checked={draft.include}
              onChange={(e) => patch(index, { include: e.target.checked })}
            />
            <input
              type="text"
              value={draft.title}
              onChange={(e) => patch(index, { title: e.target.value })}
              aria-label="Aufgabe"
            />
          </label>
          <div className="deck-fields">
            <label>
              Zuständig
              <select
                value={draft.assignUserId}
                onChange={(e) => patch(index, { assignUserId: e.target.value })}
              >
                <option value="">— niemand —</option>
                {board?.members.map((member) => (
                  <option key={member} value={member}>
                    {member}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Frist
              <input
                type="date"
                value={draft.dueDate}
                onChange={(e) => patch(index, { dueDate: e.target.value })}
              />
            </label>
            {draft.spokenDue && <span className="muted small">gesagt: „{draft.spokenDue}“</span>}
          </div>
        </div>
      ))}

      {error && <p className="error">{error}</p>}
      {done && <p className="muted">{done}</p>}

      <div className="deck-actions">
        <button className="primary" onClick={() => void submit()} disabled={busy || !stackId || !!done}>
          {busy ? 'Wird übernommen …' : 'Übernehmen'}
        </button>
        <button className="secondary" onClick={() => setOpen(false)}>
          Schließen
        </button>
      </div>
    </section>
  );
}
